/**
 * Tooltip — Generic hover tooltip for system-wide deployment.
 *
 * Props:
 *   text      {string}   Tooltip content (falsy = no tooltip, renders children only)
 *   position  {string}   "top" | "bottom" | "left" | "right" (default: "top")
 *   maxWidth  {number}   Max width in px (default: 280)
 *   delay     {number}   Delay in ms before showing (default: 300)
 *   inline    {boolean}  Render wrapper as <span> instead of <div> (default: false)
 *   variant   {string}   "dark" (default) or "light" (white bordered card, as in the PWin reference)
 *   children  {node}     The element(s) to wrap
 *
 * Styling: dark bg, white text, Tailwind classes, z-[60], pointer-events-none.
 * 150ms fade-in via opacity transition. 300ms hover delay to prevent flash.
 *
 * (PATCH-16: System-Wide Tooltip Deployment)
 */

import { useState, useRef } from "react";

const POSITION_CLASSES = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
};

const ARROW_CLASSES = {
  top: "top-full left-1/2 -translate-x-1/2 border-t-gray-900 border-x-transparent border-b-transparent border-4",
  bottom: "bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-x-transparent border-t-transparent border-4",
  left: "left-full top-1/2 -translate-y-1/2 border-l-gray-900 border-y-transparent border-r-transparent border-4",
  right: "right-full top-1/2 -translate-y-1/2 border-r-gray-900 border-y-transparent border-l-transparent border-4",
};

const BUBBLE_CLASSES = {
  dark: "bg-gray-900 text-white text-[11px] rounded-md shadow-xl px-2 py-1",
  // White card with a border, the same look as the PWin reference popover; it has no arrow.
  light: "bg-white text-gray-600 text-[10.5px] rounded-lg shadow-2xl border border-gray-300 px-3 py-2",
};

export default function Tooltip({
  text,
  position = "top",
  maxWidth = 280,
  delay = 300,
  inline = false,
  variant = "dark",
  children,
}) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  if (!text) return children;

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => setVisible(true), delay);
  };

  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    setVisible(false);
  };

  const Wrapper = inline ? "span" : "div";

  return (
    <Wrapper
      className={`relative ${inline ? "inline-flex items-center" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <span
        className={`absolute ${POSITION_CLASSES[position] || POSITION_CLASSES.top} z-[60] pointer-events-none transition-opacity duration-150 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        style={{ maxWidth: `${maxWidth}px` }}
        role="tooltip"
      >
        <span className={`block leading-snug ${BUBBLE_CLASSES[variant] || BUBBLE_CLASSES.dark}`}>
          {text}
        </span>
        {variant !== "light" && (
          <span
            className={`absolute ${ARROW_CLASSES[position] || ARROW_CLASSES.top} w-0 h-0`}
          />
        )}
      </span>
    </Wrapper>
  );
}
